import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import styles from "./styles.module.css";
import Toggle from "../../../components/ui/toggle";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "../../../components/button-v1";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getOrganizationList } from "../../../services/admin/organization";
import { validateTenoviCreds } from "../../../services/tenovi";
import toast from "react-hot-toast";
import { timeZones, states } from "../../../utils/constants";
import {
  updateOrganization,
  getOrganizationDetails,
} from "../../../services/admin/organization";
import { getUserRoleSlug } from "../../../utils/authorization";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import Spinner from "../../spinner";
import Input from "../../../components/ui/input";

const defaultValues = {
  name: "",
  invoice_recipient_email: "",
  account_biller_contact_email: "",
  timezone: "",
  city: "",
  account_state: "",
  country: "",
  acccount_phone: "",
  postal_code: "",
  address_line_1: "",
  tenovi_client_name: null,
  tenovi_api_key: null,
  tenovi_webhook_header: null,
  is_ccm: true,
  is_rpm: false,
  vital_sms_alert: false,
};

const schemaValidations = z
  .object({
    name: z.string().min(1, "Hospital name is required"),
    invoice_recipient_email: z.string().email("Email is required").optional(),
    account_biller_contact_email: z
      .string()
      .email("Email is required")
      .optional(),
    account_phone: z
      .string()
      .refine((val) => /^[0-9+\-\s()]+$/.test(val), {
        message: "Invalid phone number",
      })
      .transform((val) => val.replace(/\D/g, ""))
      .refine(
        (val) =>
          val.length === 10 || (val.length === 11 && val.startsWith("1")),
        { message: "Invalid phone number" }
      )
      .transform((val) => {
        return val.length === 11 ? val.slice(1) : val;
      }),
    timezone: z.string().min(1, "Timezone is required"),
    city: z.string().min(1, "City name is required"),
    account_state: z.string().min(1, "State is required"),
    country: z.string().optional(),
    postal_code: z.string().optional(),
    address_line_1: z.string().min(1, "Address line 1 is required"),
    is_ccm: z.boolean(),
    is_rpm: z.boolean(),
    vital_sms_alert: z.boolean().optional(),
    tenovi_client_name: z.string().nullable(),
    tenovi_api_key: z.string().nullable(),
    tenovi_webhook_header: z.string().nullable(),
  })
  .refine((data) => data.is_ccm || data.is_rpm, {
    message: "At least one program (CCM or RPM) must be selected",
    path: ["programs"],
  })
  .refine((data) => {
    if (!data.is_rpm) {
      data.tenovi_api_key = "";
      data.tenovi_client_name = "";
      return data;
    }
    return true;
  });

const AddOrganization = ({ isOpen, onClose }) => {
  const roleSlug = getUserRoleSlug();
  const showVitalSmsToggle = ["toca_admin", "toca_care_manager","toca_super_admin"].includes(
    roleSlug
  );
  const queryClient = useQueryClient();
  const organizationId = sessionStorage.getItem("acc_id");
  const [organizationDetails, setOrganizationDetails] = useState(null);

  const orgDetailsQuery = useQuery(["organization-details"], () =>
    getOrganizationDetails()
  );

  const updateOrgMutation = useMutation((data) => updateOrganization(data), {
    onSuccess: (data) => {
      toast.success("Organization updated successfully");
      queryClient.invalidateQueries(["organization-details"]);
      // The org-level Vital SMS Alert cascades to patients on the backend, so
      // cached patient data is now stale. Invalidate it so the patient Settings
      // toggle and the readings SMS indicator refetch without a manual refresh.
      queryClient.invalidateQueries({
        queryKey: ["patient-general-details"],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["patient-readings"],
        exact: false,
      });
      reset();
      onClose();
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.error || "Could not update organization"
      );
    },
  });

  const validateTenoviCredsMutation = useMutation(
    (data) => validateTenoviCreds(data),
    {
      onError: (err) => {
        toast.error(err?.response?.data?.error ?? "Invalid Tenovi Info");
      },
    }
  );

  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: defaultValues,
    resolver: zodResolver(schemaValidations),
  });

  const onSubmit = async (data) => {
    const body = {
      name: data.name,
      invoice_recipient_email: data.invoice_recipient_email,
      account_biller_email: data.account_biller_contact_email,
      phone_number: data.account_phone,
      tenovi_client: data.tenovi_client_name,
      tenovi_api_key: data.tenovi_api_key,
      timezone: data.timezone,
      city: data.city,
      state: data.account_state,
      country: data.country,
      postal_code: data.postal_code,
      address_line_1: data.address_line_1,
      is_ccm: data.is_ccm,
      is_rpm: data.is_rpm,
      vital_sms_alert: data.vital_sms_alert ?? false,
      tenovi_webhook_header: data.tenovi_webhook_header,
    };

    // if (data.tenovi_client_name && data.tenovi_api_key) {
    //   const tenoviBody = {
    //     client_name: data.tenovi_client_name,
    //     api_key: data.tenovi_api_key,
    //   };

    //   try {
    //     await validateTenoviCredsMutation.mutateAsync(tenoviBody);
    //     updateOrgMutation.mutate(body);
    //   } catch (_) {
    //     // error toast handled in mutation's onError
    //   }
    // } else {
    //   updateOrgMutation.mutate(body);
    // }

    updateOrgMutation.mutate(body);
  };

  const is_ccm = watch("is_ccm");
  const is_rpm = watch("is_rpm");
  const timezone = watch("timezone");
  const vital_sms_alert = watch("vital_sms_alert");
  const account_state = watch("account_state");

  useEffect(() => {
    if (orgDetailsQuery.data) {
      setOrganizationDetails(orgDetailsQuery.data);
    }
  }, [orgDetailsQuery.isLoading]);

  useEffect(() => {
    if (organizationDetails) {
      reset({
        name: organizationDetails.name,
        invoice_recipient_email: organizationDetails.invoice_recipient_email,
        account_biller_contact_email: organizationDetails.account_biller_email,
        account_phone: organizationDetails.phone_number,
        tenovi_client_name: organizationDetails.tenovi_client,
        tenovi_api_key: organizationDetails.tenovi_api_key,
        tenovi_webhook_header: organizationDetails.tenovi_webhook_header,
        timezone: organizationDetails.timezone,
        city: organizationDetails.city,
        account_state: organizationDetails.state,
        country: organizationDetails.country,
        postal_code: organizationDetails.postal_code,
        address_line_1: organizationDetails.address_line_1,
        is_ccm: organizationDetails.is_ccm,
        is_rpm: organizationDetails.is_rpm,
        vital_sms_alert: organizationDetails.vital_sms_alert ?? false,
      });
    }
  }, [organizationDetails]);

  return (
    <Dialog open={isOpen} setOpen={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Update Organization</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {orgDetailsQuery.isLoading ? (
            <div className="content-center">
              <Spinner />
            </div>
          ) : (
            <form className={styles.form}>
              <div className={styles.form_step}>
                <h3 className={styles.form_title}>Account Information</h3>
                <div className={styles.form_row}>
                  <div className={styles.form_group}>
                    <label>Hospital Name</label>
                    <input type="text" {...register("name")} />
                    {errors.name && (
                      <span className={styles.error}>
                        {errors.name.message}
                      </span>
                    )}
                  </div>
                  <div className={styles.form_group}>
                    <label>Invoice Recipient Email</label>
                    <input
                      type="email"
                      {...register("invoice_recipient_email")}
                    />
                    {errors.invoice_recipient_email && (
                      <span className={styles.error}>
                        {errors.invoice_recipient_email.message}
                      </span>
                    )}
                  </div>
                </div>
                <div className={styles.form_row}>
                  <div className={styles.form_group}>
                    <label>Account Biller Email</label>
                    <input
                      type="email"
                      {...register("account_biller_contact_email")}
                    />
                    {errors.account_biller_contact_email && (
                      <span className={styles.error}>
                        {errors.account_biller_contact_email.message}
                      </span>
                    )}
                  </div>
                  <div className={styles.form_group}>
                    <label>Phone</label>
                    <Controller
                      name="account_phone"
                      control={control}
                      render={({ field }) => (
                        <Input
                          type="text"
                          mask={true}
                          maskPattern="(999) 999-9999"
                          name={field.name}
                          value={field.value || ""}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          className="no-arrows"
                        />
                      )}
                    />
                    {errors.account_phone && (
                      <span className={styles.error}>
                        {errors.account_phone.message}
                      </span>
                    )}
                  </div>
                </div>

                <div className={styles.form_row}>
                  <div className={styles.form_group}>
                    <label>City</label>
                    <input type="text" {...register("city")} />
                    {errors.city && (
                      <span className={styles.error}>
                        {errors.city.message}
                      </span>
                    )}
                  </div>

                  <div className={styles.form_group}>
                    <label>State</label>
                    <Select
                      onChange={(value) => {
                        setValue("account_state", value);
                        clearErrors("account_state");
                      }}
                      value={account_state}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select state">
                          {account_state ?? "Select state"}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {states.map((state) => (
                          <SelectItem key={state} value={state}>
                            {state}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.account_state && (
                      <span className={styles.error}>
                        {errors.account_state.message}
                      </span>
                    )}
                  </div>
                </div>

                <div className={styles.form_row}>
                  <div className={styles.form_group}>
                    <label>Country</label>
                    <input type="text" {...register("country")} />
                    {errors.country && (
                      <span className={styles.error}>
                        {errors.country.message}
                      </span>
                    )}
                  </div>

                  <div className={styles.form_group}>
                    <label>Postal Code</label>
                    <input
                      type="number"
                      {...register("postal_code")}
                      className="no-arrows"
                    />
                    {errors.postal_code && (
                      <span className={styles.error}>
                        {errors.postal_code.message}
                      </span>
                    )}
                  </div>
                </div>

                <div className={styles.form_row}>
                  <div className={styles.form_group}>
                    <label>Address</label>
                    <input type="text" {...register("address_line_1")} />
                    {errors.address_line_1 && (
                      <span className={styles.error}>
                        {errors.address_line_1.message}
                      </span>
                    )}
                  </div>
                  <div className={styles.form_group}>
                    <label>Timezone</label>

                    <Select
                      onChange={(value) => {
                        setValue("timezone", value);
                        clearErrors("timezone");
                      }}
                      value={timezone}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select timezone">
                          {timeZones.find((tz) => tz.value === timezone)
                            ?.label ?? "Select timezone"}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {timeZones.map((zone) => (
                          <SelectItem key={zone.value} value={zone.value}>
                            {zone.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.timezone && (
                      <span className={styles.error}>
                        {errors.timezone.message}
                      </span>
                    )}
                  </div>
                </div>

                {showVitalSmsToggle && (
                  <div className={styles.form_row}>
                    <div className={`${styles.form_group} ${styles.toggle_group}`}>
                      <Toggle
                        label="Vital SMS Alert"
                        checked={vital_sms_alert}
                        onChange={(e) => {
                          setValue("vital_sms_alert", e.target.checked);
                        }}
                      />
                    </div>
                  </div>
                )}

                {is_rpm && (
                  <>
                    <h3 className={styles.form_second_title}>
                      Tenovi Information
                    </h3>

                    <div className={styles.form_row}>
                      <div className={styles.form_group}>
                        <label>Tenovi Client Name</label>
                        <input
                          type="text"
                          {...register("tenovi_client_name")}
                        />
                        {errors.tenovi_client_name && (
                          <span className={styles.error}>
                            {errors.tenovi_client_name.message}
                          </span>
                        )}
                      </div>
                      <div className={styles.form_group}>
                        <label>Tenovi API Key</label>
                        <input type="text" {...register("tenovi_api_key")} />
                        {errors.tenovi_api_key && (
                          <span className={styles.error}>
                            {errors.tenovi_api_key.message}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className={styles.form_row}>
                      <div className={styles.form_group}>
                        <label>Tenovi Webhook Header</label>
                        <input
                          type="text"
                          {...register("tenovi_webhook_header")}
                        />
                        {errors.tenovi_webhook_header && (
                          <span className={styles.error}>
                            {errors.tenovi_webhook_header.message}
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                )}

                <div className={styles.form_row}>
                  <div className={styles.form_group}>
                    <label>Programs</label>{" "}
                    <Toggle
                      label="CCM"
                      checked={is_ccm}
                      disabled={!is_ccm && is_rpm}
                      onChange={(e) => {
                        const newValue = e.target.checked;
                        setValue("is_ccm", newValue);
                        if (!newValue && !is_rpm) {
                          setValue("is_rpm", true);
                        }
                      }}
                    />
                    <Toggle
                      label="RPM"
                      checked={is_rpm}
                      disabled={!is_rpm && is_ccm}
                      onChange={(e) => {
                        const newValue = e.target.checked;
                        setValue("is_rpm", newValue);
                        if (!newValue && !is_ccm) {
                          setValue("is_ccm", true);
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            </form>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onClose()}>
            Close
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            loading={
              updateOrgMutation.isLoading ||
              validateTenoviCredsMutation.isLoading
            }
          >
            Update
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddOrganization;
